import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MenuComponent } from '../shared/components/menu/menu.component';
import { FooterComponent } from '../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../book-create/login-validation.component/login-validation.component';
import { MyBookItemStruct, MyBookVersionStruct } from '../shared/services/structs/my-books.struct';
import { AlertService, AlertType } from '../shared/services/alert.service';
import { MyBooksService } from '../shared/services/apis/platform/my-book.service';
import { MyBooksRequest } from '../shared/services/requests/platform/my-books.request';
import { MyBooksResponse } from '../shared/services/responses/platform/my-books.response';

@Component({
    selector: 'app-my-books',
    standalone: true,
    templateUrl: './my-books.component.html',
    styleUrl: './my-books.component.css',
    imports: [
        CommonModule,
        MenuComponent,
        FooterComponent,
        LoginValidationComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatButtonModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyBooksComponent implements OnInit {
    isLoading = false;

    // métricas
    totalBooksSold = 0;
    totalGrossRevenue = 0;
    walletAvailable = 0;

    // lista agrupada por obra
    list: MyBookItemStruct[] = [];

    constructor(
        private myBooksService: MyBooksService,
        private alertService: AlertService,
        private cdr: ChangeDetectorRef,
        private router: Router
    ) { }

    ngOnInit(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // login é validado pelo <app-login-validation>; buscamos após emitir loggedIn
    }

    onLoginValidated(isLogged: boolean) {
        if (!isLogged) return;
        this.fetch();
    }

    fetch(): void {
        this.isLoading = true;
        const req: MyBooksRequest = { page: 1, pageSize: 100 };

        this.myBooksService.postMyBooks(req).subscribe({
            next: (res: MyBooksResponse) => {
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Falha ao carregar seus livros.', AlertType.error);
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    return;
                }
                this.totalBooksSold = res.totalBooksSold ?? 0;
                this.totalGrossRevenue = res.totalGrossRevenue ?? 0;
                this.walletAvailable = res.walletAvailable ?? 0;
                this.list = res.list ?? [];
                

                const PLACEHOLDER = 'assets/images/book-placeholder.svg';

                this.list = (res.list ?? []).map(item => ({
                    ...item,
                    versions: item.versions?.map(v => ({
                        ...v,
                        coverUrl: (v.coverUrl && v.coverUrl.length ? v.coverUrl : PLACEHOLDER)
                    })) ?? []
                }));

                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: (err) => {
                this.alertService.show('Erro', 'Erro inesperado ao carregar a lista.', AlertType.error);
                this.isLoading = false;
                this.cdr.markForCheck();
            }
        });
    }

    onImgError(ev: Event) {
        const el = ev?.target as HTMLImageElement;
        if (el) el.src = 'assets/images/book-placeholder.png';
    }

    // reduz re-render no *ngFor
    trackByVersion = (_: number, v: any) => v?.idBookVersion ?? _;
    trackByBook = (_: number, b: any) => b?.idBook ?? _;

    // ações
    goTo(url: string) {
        // as rotas já vêm prontas do backend
        if (!url) return;
        this.router.navigateByUrl(url);
    }

    // helpers de UI
    coverSrc(url?: string | null): string {
        return url && url.length ? url : 'assets/images/book-placeholder.png';
    }

    statusClass(status: string): string {
        const key = (status || '').toLowerCase();
        if (key.includes('publica')) return 'chip chip-published';
        if (key.includes('revis')) return 'chip chip-review';
        if (key.includes('aprova')) return 'chip chip-pending-approval';
        if (key.includes('pagamento')) return 'chip chip-payment';
        if (key.includes('pausad')) return 'chip chip-paused';
        if (key.includes('reprov')) return 'chip chip-rejected';
        return 'chip chip-edit';
    }

    // tooltips de ação vindos do backend
    editTooltip(v: MyBookVersionStruct) { return v.actions?.editTooltip || 'Editar'; }
    metricsTooltip(v: MyBookVersionStruct) { return v.actions?.metricsTooltip || 'Métricas'; }
    toggleTooltip(v: MyBookVersionStruct) { return v.actions?.toggleSalesTooltip || 'Pausar/Retomar vendas'; }
}
